import { Popup } from 'react-map-gl/maplibre';
import { TriangleAlert } from 'lucide-react';
import { Badge, Separator, cn } from 'shared-ui';

import {
    AIR_VALVE_DATE_PLACEHOLDER,
    AIR_VALVE_FIELD_PLACEHOLDER,
    formatAirValveDate,
} from '../utils/airValveFeatureProperties';
import type { AirValveFeatureProperties, HighRiskAirValveFeatureProperties } from '../utils/airValveFeatureProperties';
import { MARKER_ANCHORED_POPUP_OFFSET } from '../utils/markerDimensions';
import { HoverCardField } from './HoverCardField';
import { What3WordsField } from './What3WordsField';
import { MapNavButtons } from './MapNavButtons';

interface AirValvePopupProps {
    valve: AirValveFeatureProperties;
    /** Matching high-risk record (by asset name) when the valve is high-risk; supplies date/reasons. */
    highRiskDetail?: HighRiskAirValveFeatureProperties | null;
    /** Geographic [longitude, latitude] — the popup anchor and the navigation deep-links. */
    coords: [number, number];
    /** Wired to the air-valve interactions so hovering onto the card cancels its dismiss timer. */
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

/**
 * Air-valve detail card, positioned by MapLibre's own `<Popup>`: it projects the lng/lat, tracks
 * it on pan/zoom, picks a side that fits the viewport (`anchor` unset = auto), and clears the
 * marker via a per-anchor `offset`. Interactive (no `--passive`) so the cursor can travel onto
 * the card and use its nav buttons. The high-risk variant uses a rem min-height so a long Reason
 * can grow the card; MapLibre re-measures the DOM, so positioning adapts. Card width is rem with
 * no pixel math; popup chrome is stripped via `.orbit-hover-popup`.
 */
export function AirValvePopup({ valve, highRiskDetail, coords, onMouseEnter, onMouseLeave }: AirValvePopupProps) {
    const client = valve.sc ?? AIR_VALVE_FIELD_PLACEHOLDER;
    const assetNumber = valve.name || AIR_VALVE_FIELD_PLACEHOLDER;
    const dma = valve.dma || AIR_VALVE_FIELD_PLACEHOLDER;

    return (
        <Popup
            longitude={coords[0]}
            latitude={coords[1]}
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={MARKER_ANCHORED_POPUP_OFFSET}
            maxWidth="none"
            className="orbit-hover-popup"
        >
            <div
                // High-risk cards use a min-height so a long Reason can grow the card; normal cards are fixed.
                className={cn(
                    'border-border/30 bg-card flex w-[19.6875rem] flex-col gap-6 rounded-md border p-5 shadow-lg',
                    valve.highRisk ? 'min-h-[26.9375rem]' : 'h-[19.5rem]',
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Title */}
                <p className="text-foreground text-xl leading-tight font-black uppercase">Air Valve</p>

                {/* High Risk badge — negative margin trims the section gap to a tighter, equal gap above and below */}
                {valve.highRisk && (
                    <div className="-my-3">
                        <Badge
                            variant="destructive"
                            className="bg-destructive text-xs leading-4 font-medium text-white"
                        >
                            <TriangleAlert aria-hidden="true" strokeWidth={3} />
                            High Risk
                        </Badge>
                    </div>
                )}

                <Separator className="bg-border" />

                {/* Asset info */}
                <div className="flex flex-col gap-3">
                    {valve.highRisk && (
                        <HoverCardField
                            label="Air Valve What3words"
                            meta="(Actual)"
                            value={<What3WordsField value={valve.w3w ?? highRiskDetail?.w3w} />}
                        />
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <HoverCardField label="Client" value={client} />
                        <HoverCardField label="Asset Number" value={assetNumber} />
                        <HoverCardField label="DMA" value={dma} />
                        {valve.highRisk && (
                            <HoverCardField
                                label="Date Identified"
                                value={formatAirValveDate(highRiskDetail?.identified) ?? AIR_VALVE_DATE_PLACEHOLDER}
                            />
                        )}
                    </div>

                    {/* Full-width so a long reason wraps and the card grows in height */}
                    {valve.highRisk && (
                        <HoverCardField label="Reason" value={highRiskDetail?.reasons ?? AIR_VALVE_FIELD_PLACEHOLDER} />
                    )}
                </div>

                <Separator className="bg-border" />

                <MapNavButtons coords={coords} />
            </div>
        </Popup>
    );
}
